import React from 'react';
import { motion } from 'framer-motion';
import { Filter, X } from 'lucide-react';

const SearchFilters = ({ 
  selectedCategory, 
  setSelectedCategory,
  selectedDifficulty,
  setSelectedDifficulty,
  tutorials
}) => {
  // Extract unique categories from tutorials
  const categories = ['all', ...new Set(tutorials?.map(t => t?.category))];
  const difficulties = ['all', 'Beginner', 'Intermediate', 'Advanced', 'Expert'];
  
  const getCategoryCount = (category) => {
    if (category === 'all') return tutorials?.length;
    return tutorials?.filter(t => t?.category === category)?.length;
  };
  
  const getDifficultyCount = (difficulty) => {
    if (difficulty === 'all') return tutorials?.length;
    return tutorials?.filter(t => t?.difficulty === difficulty)?.length;
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedDifficulty('all');
  };

  const hasActiveFilters = selectedCategory !== 'all' || selectedDifficulty !== 'all';

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-6 p-4 bg-card border border-border rounded-lg"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4" />
          <span className="font-medium">Filters</span>
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:underline flex items-center space-x-1"
          >
            <X className="w-3 h-3" />
            <span>Clear All</span>
          </button>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Category Filter */}
        <div>
          <h4 className="font-medium mb-3">Category</h4>
          <div className="space-y-2">
            {categories?.map((category) => (
              <label
                key={category}
                className="flex items-center space-x-3 cursor-pointer group"
              >
                <input
                  type="radio"
                  name="category"
                  value={category}
                  checked={selectedCategory === category}
                  onChange={() => setSelectedCategory(category)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm group-hover:text-blue-600 transition-colors flex-1">
                  {category === 'all' ? 'All Categories' : category}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({getCategoryCount(category)})
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Difficulty Filter */}
        <div>
          <h4 className="font-medium mb-3">Difficulty Level</h4>
          <div className="space-y-2">
            {difficulties?.map((difficulty) => (
              <label
                key={difficulty}
                className="flex items-center space-x-3 cursor-pointer group"
              >
                <input
                  type="radio"
                  name="difficulty"
                  value={difficulty}
                  checked={selectedDifficulty === difficulty}
                  onChange={() => setSelectedDifficulty(difficulty)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm group-hover:text-blue-600 transition-colors flex-1">
                  {difficulty === 'all' ? 'All Levels' : difficulty}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({getDifficultyCount(difficulty)})
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {selectedCategory !== 'all' && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center space-x-2">
                <span>Category: {selectedCategory}</span>
                <button
                  onClick={() => setSelectedCategory('all')}
                  className="hover:bg-blue-200 rounded-full p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {selectedDifficulty !== 'all' && (
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm flex items-center space-x-2">
                <span>Level: {selectedDifficulty}</span>
                <button
                  onClick={() => setSelectedDifficulty('all')}
                  className="hover:bg-purple-200 rounded-full p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SearchFilters;